/**
 * PractitionerSettingsSection — Settings entry point for the Practitioner Portal.
 *
 * Shown ONLY when `isPractitioner === true` (gated in settings.tsx).
 * Displays practitioner identity, certification badge, stats, and
 * a gradient CTA to open the practitioner dashboard.
 *
 * Design reference: screen5-settings-entry.png (Pencil export)
 * HTML prototype: practitioner-portal-v2-settings-entry.html
 *
 * Styling rules:
 * - StyleSheet.create only (no NativeWind)
 * - Purple (#A78BFA) left border on GlassCard container
 * - ClientAvatar (gradient initials circle) for practitioner avatar
 * - Gold badge for certified practitioners
 * - GlassButton (compact, theatrical variant) for the dashboard CTA
 */

import { useCallback } from 'react';
import { View, Text, StyleSheet, ViewStyle } from 'react-native';
import { useTranslation } from '@dramadetector/shared';
import { TierBody } from '@dramadetector/ui';
import { GlassCard } from '../../glass/GlassCard';
import { GlassButton } from '../../glass/GlassButton';
import { ClientAvatar } from '../practitioner-dashboard/ClientAvatar';
import type { PractitionerProfile } from '../../../hooks/usePractitionerProfile';
import { getInitials } from '../../../hooks/practitioner-utils';

interface PractitionerSettingsSectionProps {
  profile: PractitionerProfile;
  activeClients?: number;
  monthlyEarnings?: string;
  onOpenDashboard: () => void;
}

function practiceTypeLabel(type: PractitionerProfile['practiceType']): string {
  const labels: Record<PractitionerProfile['practiceType'], string> = {
    therapist: 'Therapist',
    coach: 'Coach',
    counselor: 'Counselor',
    other: 'Practitioner',
  };
  return labels[type] ?? 'Practitioner';
}

export function PractitionerSettingsSection({
  profile,
  activeClients,
  monthlyEarnings,
  onOpenDashboard,
}: PractitionerSettingsSectionProps) {
  const { t } = useTranslation('settings');

  const handlePress = useCallback(() => {
    onOpenDashboard();
  }, [onOpenDashboard]);

  const initials = getInitials(profile.practiceName);
  const isCertified = profile.certificationStatus === 'certified';

  return (
    <View style={styles.wrapper}>
      <TierBody size="xs" weight="semibold" className="text-gray-500 uppercase px-4 mb-2">
        {t('practitioner_section.title')}
      </TierBody>

      {/* Purple left border applied via wrapping View — GlassCard does not support borderLeft prop */}
      <View style={styles.purpleBorderWrapper}>
        <GlassCard noPadding>
          {/* Practitioner identity row */}
          <View style={styles.identityContainer}>
            <View style={styles.avatarRow}>
              <ClientAvatar initials={initials} gradientIndex={0} size={48} />
              <View style={styles.nameBlock}>
                <View style={styles.nameRow}>
                  <Text style={styles.practiceName}>{profile.practiceName}</Text>
                  {isCertified && (
                    <View style={styles.certBadge}>
                      <Text style={styles.certBadgeText}>
                        {t('practitioner_section.certified')}
                      </Text>
                    </View>
                  )}
                </View>
                <View style={styles.practiceTypePill}>
                  <Text style={styles.practiceTypeText}>
                    {practiceTypeLabel(profile.practiceType)}
                  </Text>
                </View>
              </View>
            </View>

            {/* Stats row */}
            <View style={styles.statsRow}>
              <Text style={styles.statsText}>
                {t('practitioner_section.stats', {
                  clients: activeClients ?? 0,
                  earnings: monthlyEarnings ?? '$0',
                })}
              </Text>
            </View>
          </View>

          {/* Divider */}
          <View style={styles.divider} />

          {/* CTA button */}
          <View style={styles.ctaContainer}>
            <GlassButton
              variant="theatrical"
              size="compact"
              onPress={handlePress}
              style={styles.ctaButton}
              testID="open-practitioner-dashboard-button"
            >
              {t('practitioner_section.open_dashboard')}
            </GlassButton>
          </View>
        </GlassCard>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  wrapper: {
    marginTop: 16,
  } as ViewStyle,
  purpleBorderWrapper: {
    borderLeftWidth: 3,
    borderLeftColor: '#A78BFA',
    borderRadius: 16,
    overflow: 'hidden',
  } as ViewStyle,
  identityContainer: {
    padding: 16,
    paddingBottom: 12,
  } as ViewStyle,
  avatarRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    marginBottom: 12,
  } as ViewStyle,
  nameBlock: {
    flex: 1,
    minWidth: 0,
    gap: 6,
  } as ViewStyle,
  nameRow: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 6,
  } as ViewStyle,
  practiceName: {
    fontSize: 14,
    fontWeight: '700',
    color: '#1e293b',
  },
  certBadge: {
    backgroundColor: '#fbbf24',
    borderRadius: 999,
    paddingHorizontal: 8,
    paddingVertical: 2,
    shadowColor: '#f59e0b',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.35,
    shadowRadius: 4,
    elevation: 2,
  } as ViewStyle,
  certBadgeText: {
    color: '#ffffff',
    fontSize: 10,
    fontWeight: '700',
  },
  practiceTypePill: {
    alignSelf: 'flex-start',
    backgroundColor: '#ede9fe',
    borderRadius: 999,
    paddingHorizontal: 8,
    paddingVertical: 2,
  } as ViewStyle,
  practiceTypeText: {
    color: '#7c3aed',
    fontSize: 11,
    fontWeight: '600',
  },
  statsRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 4,
  } as ViewStyle,
  statsText: {
    fontSize: 12,
    color: '#475569',
    fontWeight: '500',
  },
  divider: {
    height: 1,
    backgroundColor: 'rgba(255,255,255,0.6)',
    marginHorizontal: 0,
  } as ViewStyle,
  ctaContainer: {
    padding: 16,
  } as ViewStyle,
  ctaButton: {
    alignSelf: 'stretch',
    borderRadius: 14,
  } as ViewStyle,
});
